import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { linkPage } from "@utils/router";

import { useQuery } from "@tanstack/react-query";
import { SchoolService } from "@services/school.service";
import { HomeService } from "@services/home.service";
import { Spiner } from "@components/loaders/spiner";
import { toRp } from "@utils/helper";
import { Mading } from "@services/dtos/home.dto";
import { useThemeStore } from "@stores/theme.store";
import { useInboxStore } from "@stores/inbox.store";
// import { androidIsAvailableBiometric } from "@utils/android"; // biometric belum terpakai
import moment from "moment";

import LogoHomePage from "@assets/images/logo-deha.png";
import {
  LuAlertTriangle,
  LuArrowRightLeft,
  LuBell,
  LuBookOpen,
  LuCalendarDays,
  LuChevronRight,
  LuClipboardList,
  LuHistory,
  LuLayoutGrid,
  LuReceipt,
  LuStethoscope,
  LuTrophy,
  LuUser,
} from "react-icons/lu";
import { PopupError } from "@components/general/popup";

const ActionIcon = ({
  onClick,
  fill,
  Icon,
  label,
}: {
  onClick: () => void;
  fill: string;
  Icon: React.ElementType;
  label: string;
}) => (
  <div
    onClick={onClick}
    className="tw-flex tw-flex-col tw-items-center tw-gap-1.5 tw-cursor-pointer"
  >
    <div
      className="tw-flex tw-items-center tw-justify-center tw-rounded-full tw-w-12 tw-h-12 tw-shadow-sm"
      style={{ background: fill }}
    >
      <Icon className="tw-text-xl tw-text-white" />
    </div>
    <div className="tw-text-[10px] tw-font-medium tw-text-text-primary tw-text-center tw-line-clamp-1">
      {label}
    </div>
  </div>
);

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { setAppBar } = useThemeStore((state) => state);
  const { unreadInbox } = useInboxStore((state) => state);

  const [popupError, setPopupError] = useState<boolean>(false);

  const kategori = "*";
  const { data: dataNis } = useQuery({
    queryKey: [SchoolService.queries.GET_SCHOOL_STUDENT_BIO],
    queryFn: SchoolService.studentBio,
  });
  const nis = dataNis?.data?.nis ?? "";
  // const gender = dataNis?.data?.gender ?? "";

  const { data: dataUangSaku, isPending: isPendingUangSaku } = useQuery({
    queryKey: [HomeService.queries.GET_BILLS_AND_BALANCES, nis],
    queryFn: () => HomeService.billsAndBalances({ nis }),
  });

  // const { data, isPending } = useQuery({
  //   queryKey: [SchoolService.queries.GET_SCHOOL_STUDENT_BIOGRAPHY, nis],
  //   queryFn: () => SchoolService.studentBiography(nis),
  // });

  const { data: dataMading, isPending: isPendingMading } = useQuery({
    queryKey: [HomeService.queries.GET_MADING, kategori, "5"],
    queryFn: () => HomeService.mading(kategori, "5"),
  });

  // Biometric availability check — belum terpakai, dinonaktifkan sementara
  // useEffect(() => {
  //   const setupBiometric = setTimeout(() => {
  //     localStorage.setItem("is-biometric", "false");
  //     androidIsAvailableBiometric();
  //   }, 1000);
  //
  //   return () => {
  //     clearTimeout(setupBiometric);
  //   };
  // }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setAppBar({
      title: "Beranda",
      show: true,
      leading: "home",
      actions: [
        <div
          key="inbox"
          className="tw-relative tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-xl tw-bg-surface-100 tw-cursor-pointer"
          onClick={() => navigate(linkPage.INBOX_PAGE)}
        >
          <LuBell size={14} className="tw-text-primary" />
          {unreadInbox && (
            <span className="tw-absolute tw-top-1 tw-right-1 tw-w-2 tw-h-2 tw-rounded-full tw-bg-red-500" />
          )}
        </div>,
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAppBar, unreadInbox]);

  const openDetailMading = (detailMading: Mading) => {
    navigate(linkPage.MADING_DETAIL, { state: detailMading });
  };

  return (
    <>
      {/* Hero blob: logo, avatar, saldo */}
      <div
        className="dash-hero tw-relative tw-overflow-hidden tw-text-center"
        style={{
          background:
            "linear-gradient(160deg, #0f5fa8 0%, #56b6fd 60%, #2e9df0 100%)",
        }}
      >
        <div
          className="tw-absolute tw-top-[-34px] tw-right-[-34px] tw-w-40 tw-h-40 tw-rounded-full"
          style={{ background: "rgba(255,255,255,0.08)" }}
        />
        <div
          className="tw-absolute tw-bottom-6 tw-left-[-26px] tw-w-24 tw-h-24 tw-rounded-full"
          style={{ background: "rgba(255,255,255,0.07)" }}
        />

        <div className="tw-relative tw-z-10 tw-px-4 tw-pt-5 tw-pb-9">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-8">
            <div className="tw-flex tw-items-center tw-gap-2.5">
              <img
                src={LogoHomePage}
                alt="Logo Darul Hidayah"
                className="tw-h-[46px] tw-w-[46px] tw-object-contain"
                style={{ filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.25))" }}
              />
              <div className="tw-text-left">
                <div className="tw-text-white tw-text-sm tw-font-bold tw-leading-tight">
                  Darul Hidayah
                </div>
                <div className="tw-text-white/70 tw-text-[10px] tw-leading-tight">
                  Rasau Jaya, Kubu Raya
                </div>
              </div>
            </div>
            <div
              onClick={() => navigate(linkPage.HOME_PROFILE)}
              className="tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-full tw-cursor-pointer tw-border tw-border-white/30 tw-shrink-0"
              style={{ background: "rgba(255,255,255,0.15)" }}
            >
              <LuUser className="tw-text-white tw-text-base" />
            </div>
          </div>

          <div className="tw-text-white/70 tw-text-xs tw-mb-1 tw-tracking-wide">
            Uang Saku Tersisa
          </div>
          <div className="tw-text-3xl tw-font-bold tw-text-white tw-mb-6">
            {isPendingUangSaku ? (
              <Spiner />
            ) : (
              toRp(dataUangSaku?.data?.saldo ?? 0)
            )}
          </div>

          <div className="tw-flex tw-justify-center tw-gap-3">
            {/* <div
              onClick={() =>
                navigate(
                  linkPage.FINANCE_REQUEST_VA.replace(":type", "topup"),
                  {
                    state: { tagihan: false, tipe: "topup" },
                  },
                )
              }
              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-1.5 tw-rounded-full tw-cursor-pointer"
              style={{ background: "rgba(255,255,255,0.18)" }}
            >
              <LuArrowRightLeft className="tw-text-white tw-text-sm" />
              <span className="tw-text-white tw-text-xs tw-font-medium">
                Top-up
              </span>
            </div> */}
            <div
              onClick={() => navigate(linkPage.FINANCE_POCKET)}
              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-1.5 tw-rounded-full tw-cursor-pointer"
              style={{ background: "rgba(255,255,255,0.18)" }}
            >
              <LuHistory className="tw-text-white tw-text-sm" />
              <span className="tw-text-white tw-text-xs tw-font-medium">
                Riwayat
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* end of Hero blob */}

      {/* Aksi menu: ikon bulat solid */}
      <div className="tw-px-4 tw-pt-6 tw-pb-2">
        <div className="tw-grid tw-grid-cols-4 tw-gap-y-5">
          {/* <ActionIcon
            onClick={() => navigate(linkPage.MENU_FINANCE)}
            fill="#4D8EFF"
            Icon={LuReceipt}
            label="Kewajiban"
          /> */}
          <ActionIcon
            onClick={() => navigate(linkPage.PERMIT_DATA_PAGE)}
            fill="#ff6b6b"
            Icon={LuCalendarDays}
            label="Laporan Izin"
          />
          <ActionIcon
            onClick={() => navigate(linkPage.INFO_BIOGRAPH)}
            fill="#56b6fd"
            Icon={LuUser}
            label="Biodata"
          />
          <ActionIcon
            onClick={() => navigate(linkPage.HEALTH_PAGE)}
            fill="#10b981"
            Icon={LuStethoscope}
            label="Info UKP"
          />
          <ActionIcon
            onClick={() => navigate("/school/tafaquh")}
            fill="#6366f1"
            Icon={LuBookOpen}
            label="Tafaquh"
          />
          <ActionIcon
            onClick={() => navigate(linkPage.VIOLATION_PAGE)}
            fill="#ef4444"
            Icon={LuAlertTriangle}
            label="Pelanggaran"
          />
          <ActionIcon
            onClick={() => navigate(linkPage.ACHIEVEMENT_PAGE)}
            fill="#f59e0b"
            Icon={LuTrophy}
            label="Prestasi"
          />
          {/* <ActionIcon
            onClick={() => navigate("/menu/other-menu")}
            fill="#6b7280"
            Icon={LuLayoutGrid}
            label="Lainnya"
          /> */}
          <ActionIcon
            onClick={() => navigate("/school/tahfidz")}
            fill="#6b7280"
            Icon={LuBookOpen}
            label="Tahfidz"
          />
          <ActionIcon
            onClick={() => navigate("/school/tahfidz")}
            fill="#ff6b6b"
            Icon={LuClipboardList}
            label="Absensi"
          />
        </div>
      </div>
      {/* end of Aksi menu */}

      {/* Tagihan hari ini */}
      <div className="tw-px-4 tw-mt-4 tw-mb-2">
        <div
          className="card-body tw-flex tw-items-center tw-gap-3 tw-cursor-pointer"
          style={{ borderLeft: "3px solid #ef4444" }}
          onClick={() => navigate(linkPage.MENU_FINANCE)}
        >
          <div className="tw-flex-1">
            <div className="tw-text-xs tw-text-text-secondary">
              Total Kewajiban Hari Ini
            </div>
            <div className="tw-text-base tw-font-bold tw-text-red-500">
              {isPendingUangSaku ? (
                <Spiner />
              ) : (
                toRp(dataUangSaku?.data?.tagihan ?? 0)
              )}
            </div>
          </div>
          <LuChevronRight className="tw-text-text-tertiary tw-text-lg" />
        </div>
      </div>
      {/* end of Tagihan */}

      {/* Informasi Terkini: carousel horizontal */}
      {isPendingMading ? (
        <div className="tw-mt-5 tw-py-3 tw-m-auto tw-text-center">
          <Spiner />
        </div>
      ) : (dataMading?.data ?? []).length === 0 ? (
        <></>
      ) : (
        <div className="tw-pb-20 tw-mt-3">
          <div className="tw-px-4 tw-pb-3 tw-flex tw-justify-between tw-items-center">
            <div className="tw-text-base tw-font-semibold tw-text-text-primary">
              Informasi Terkini
            </div>
            <div
              onClick={() => navigate("/mading/list")}
              className="tw-text-xs tw-text-accent-primary tw-cursor-pointer"
            >
              Lihat Semua
            </div>
          </div>
          <div
            className="tw-flex tw-gap-3 tw-overflow-x-auto tw-pb-1"
            style={{
              paddingLeft: 16,
              paddingRight: 16,
            }}
          >
            {(dataMading?.data ?? [])
              .sort((a, b) => moment(b.tanggal).diff(moment(a.tanggal)))
              .slice(0, 6)
              .map((mading, idx) => (
                <div
                  key={idx}
                  onClick={() => openDetailMading(mading)}
                  className="tw-cursor-pointer tw-rounded-2xl tw-overflow-hidden tw-bg-background-100 tw-border tw-border-background-300 tw-shadow-sm tw-shrink-0 tw-w-[67%]"
                >
                  <div
                    className="tw-w-full tw-h-[130px] tw-bg-background-300"
                    style={{
                      backgroundImage: `url(${mading.gambar})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  <div className="tw-p-2.5">
                    <div className="tw-text-xs tw-font-semibold tw-text-text-primary tw-line-clamp-2 tw-mb-1">
                      {mading.judul}
                    </div>
                    <div className="tw-text-[10px] tw-text-text-tertiary">
                      {moment(mading.tanggal ?? Date.now()).format(
                        "DD MMM YYYY",
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
      {/* end of Informasi Terkini */}

      <PopupError
        open={popupError}
        title="Fitur Tidak Tersedia"
        message="Fitur ini sedang dalam perbaikan. Kami akan segera mengaktifkannya kembali."
        textConfirm="Tutup"
        onConfirm={() => setPopupError(false)}
      />
    </>
  );
};
